import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { InstallProvider } from "@/context/InstallContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";import MyDogs from "@/pages/MyDogs";
import DogForm from "@/pages/DogForm";
import Drawer from "@/pages/Drawer";
import Missions from "@/pages/Missions";
import MissionsHub from "@/pages/MissionsHub";
import Missioni from "@/pages/Missioni";
import MissionDetail from "@/pages/MissionDetail";
import Match from "@/pages/Match";
import Home from "@/pages/Home";
import Notifications from "@/pages/Notifications";
import UserProfile from "@/pages/UserProfile";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import PublicPassport from "@/pages/PublicPassport";
import PublicUserProfile from "@/pages/PublicUserProfile";
import AppHub from "@/pages/AppHub";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import AdminDashboard from "@/pages/AdminDashboard";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DataDeletion from "@/pages/DataDeletion";
import SafeZones from "@/pages/SafeZones";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import { ShelterDetail, ShelterCandidacies } from "@/pages/Shelters";
import PresencePinger from "@/components/PresencePinger";
import ShelterDashboard from "@/pages/ShelterDashboard";
import ShelterPage from "@/pages/ShelterPage";
import ShelterDetailV2 from "@/pages/ShelterDetailV2";
import ShelterChat from "@/pages/ShelterChat";
import MessagesInbox from "@/pages/MessagesInbox";
import AppShell from "@/components/AppShell";
import SplashScreen from "@/components/SplashScreen";
import SmartInstallBanner from "@/components/SmartInstallBanner";
import ProPreview from "@/components/ProPreview";
import WalkingTracker from "@/pages/WalkingTracker";
import GlobalProPaywall from "@/components/GlobalProPaywall";
import "@/App.css";

function Protected({ children, requireAdmin }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-sm text-white/60">Caricamento...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (requireAdmin && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

// Intercepts the URL fragment right at the router root. When Emergent OAuth
// redirects the browser to /app#session_id=..., this component takes over
// BEFORE Protected runs, avoiding a bounce to /login while the cookie is
// still being minted. `useLocation().hash` is the reactive source of truth
// (window.location.hash is not reactive after history.replaceState).
function AuthHashInterceptor({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

export default function App() {
  const location = typeof window !== "undefined" ? window.location : { pathname: "" };
  const isLanding = location.pathname === "/";
  // /hub is our Netflix-style launcher — it has its own big hero background
  // and any additional wave splash on top just feels like "loading forever".
  // Skip the SplashScreen when the app boots directly on /hub.
  const isHub = location.pathname === "/hub" || location.pathname === "/hub/";
  const [splash, setSplash] = useState(() => !isLanding && !isHub && !sessionStorage.getItem("orio_splash_shown"));
  const dismissSplash = () => {
    sessionStorage.setItem("orio_splash_shown", "1");
    setSplash(false);
  };
  return (
    <AppErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <InstallProvider>
        <Toaster
          position="top-center"
          theme="light"
          richColors
          closeButton
          toastOptions={{
            style: {
              background: "#FFFFFF",
              color: "#1A1A1A",
              border: "1px solid #F0EBE3",
              boxShadow: "0 12px 28px -8px rgba(0,0,0,0.18)",
              fontSize: "13.5px",
              fontWeight: 500,
              padding: "12px 14px",
              borderRadius: "16px",
            },
          }}
        />
        <PresencePinger />
        {splash && <SplashScreen onDone={dismissSplash} />}
        {/* Smart install banner — shows after ~18s, dismissible, hidden if installed.
            Position: above the Emergent platform badge (bottom-right). */}
        <SmartInstallBanner />
        {/* Global Pro paywall — catches all backend 402 responses (Pro-only APIs) */}
        <GlobalProPaywall />
        <AuthHashInterceptor>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/passport/:dogId" element={<PublicPassport />} />
          <Route path="/hub" element={<AppHub />} />
          <Route
            path="/app/u/:userId"
            element={<Protected><AppShell><PublicUserProfile /></AppShell></Protected>}
          />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/cancella-account" element={<DataDeletion />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/app"
            element={
              <Protected>
                <AppShell>
                  <Home />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/mappa"
            element={
              <Protected>
                <AppShell>
                  <Dashboard />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/cani"
            element={
              <Protected>
                <AppShell>
                  <MyDogs />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/cani/nuovo"
            element={
              <Protected>
                <AppShell>
                  <DogForm />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/cani/:dogId"
            element={
              <Protected>
                <AppShell>
                  <DogForm />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/passeggiate"
            element={
              <Protected>
                <AppShell>
                  <ProPreview feature="il Walking Tracker" description="Traccia le tue passeggiate con GPS, andatura, calorie del cane e del padrone. Costruisci lo streak e guadagni Karma. Disponibile per gli abbonati ORIO Pro.">
                    <WalkingTracker />
                  </ProPreview>
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/cassetto"
            element={
              <Protected>
                <AppShell>
                  <Drawer />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/missioni"
            element={
              <Protected>
                <AppShell>
                  <Missioni />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/missioni/hub"
            element={
              <Protected>
                <AppShell>
                  <MissionsHub />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/missioni/rifugio"
            element={
              <Protected>
                <AppShell>
                  <ShelterDashboard />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/missioni/:missionId"
            element={
              <Protected>
                <AppShell>
                  <MissionDetail />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/match"
            element={
              <Protected>
                <AppShell>
                  <ProPreview feature="il Match passeggiate" description="Trova compagni di passeggiata compatibili con il tuo cane nella tua zona. Disponibile per gli abbonati ORIO Pro.">
                    <Match />
                  </ProPreview>
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/prevenzione"
            element={
              <Protected>
                <AppShell>
                  <ProPreview feature="il Sistema di Prevenzione anti-fuga" description="ORIO impara le tue rotte abituali e ti avvisa se ti stai allontanando lasciando il cane indietro. Disponibile per gli abbonati ORIO Pro.">
                    <SafeZones />
                  </ProPreview>
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/notifiche"
            element={
              <Protected>
                <AppShell>
                  <Notifications />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/app/profilo"
            element={
              <Protected>
                <AppShell>
                  <UserProfile />
                </AppShell>
              </Protected>
            }
          />
          <Route path="/app/rifugi" element={<Navigate to="/app/missioni?tab=rifugi" replace />} />
          <Route path="/app/rifugi/me/candidature" element={<Protected><AppShell><ShelterCandidacies /></AppShell></Protected>} />
          <Route path="/app/rifugi/:sid" element={<Protected><AppShell><ShelterDetail /></AppShell></Protected>} />
          {/* New shelter (v2) — self-service registration + admin verify + adoption dogs + donation missions */}
          <Route path="/app/rifugio" element={<Protected><AppShell><ShelterPage /></AppShell></Protected>} />
          <Route path="/app/rifugio/registra" element={<Protected><AppShell><ShelterPage /></AppShell></Protected>} />
          <Route path="/app/rifugio/chat/:tid" element={<Protected><AppShell><ShelterChat /></AppShell></Protected>} />
          <Route path="/app/rifugio/:sid" element={<Protected><AppShell><ShelterDetailV2 /></AppShell></Protected>} />
          <Route path="/app/messaggi" element={<Protected><AppShell><MessagesInbox /></AppShell></Protected>} />
          <Route
            path="/billing"
            element={
              <Protected>
                <AppShell>
                  <Billing />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/billing/success"
            element={
              <Protected>
                <AppShell>
                  <BillingSuccess />
                </AppShell>
              </Protected>
            }
          />
          <Route
            path="/admin"
            element={
              <Protected requireAdmin>
                <AppShell>
                  <AdminDashboard />
                </AppShell>
              </Protected>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AuthHashInterceptor>
        </InstallProvider>
      </AuthProvider>
    </BrowserRouter>
    </AppErrorBoundary>
  );
}
